import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  ArrowLeft,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  Clock,
  Copy,
  ExternalLink,
  Loader2,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { StatusBanner } from "@/components/ui/status-banner";
import { cn } from "@/lib/cn";
import {
  deriveStatus,
  statusLabel,
  statusVariant,
} from "@/lib/assignmentStatus";
import { resendInvitation } from "@/lib/invitations";
import { supabase } from "@/lib/supabase";
import type {
  Assignment,
  Company,
  Exercise,
  Search as JobSearch,
  SubmissionRow,
} from "@/lib/types";

export const Route = createFileRoute("/invitations_/$assignmentId")({
  component: AssignmentDetailPage,
});

function AssignmentDetailPage() {
  const { assignmentId } = Route.useParams();
  return (
    <LoginGate
      title="DeepDive"
      subtitle="Iniciá sesión para ver el detalle."
    >
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <AssignmentDetail assignmentId={assignmentId} />
        </AppShell>
      )}
    </LoginGate>
  );
}

function AssignmentDetail({ assignmentId }: { assignmentId: string }) {
  const qc = useQueryClient();
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    title: string;
    detail?: string;
  } | null>(null);

  const { data: assignment, isLoading: loadingAssignment, error } = useQuery({
    queryKey: ["assignment", assignmentId],
    queryFn: async (): Promise<Assignment> => {
      const { data, error } = await supabase
        .from("assignments")
        .select("*")
        .eq("id", assignmentId)
        .single();
      if (error) throw error;
      return data as Assignment;
    },
  });

  const { data: exercises = [] } = useQuery({
    queryKey: ["exercises", "for-assignment", assignment?.exercise_ids],
    queryFn: async () => {
      if (!assignment) return [];
      const { data, error } = await supabase
        .from("exercises")
        .select("*")
        .in("id", assignment.exercise_ids);
      if (error) throw error;
      return (data ?? []) as Exercise[];
    },
    enabled: !!assignment,
  });

  const { data: company } = useQuery({
    queryKey: ["company", assignment?.company_id],
    queryFn: async (): Promise<Company | null> => {
      if (!assignment?.company_id) return null;
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, rubro")
        .eq("id", assignment.company_id)
        .single();
      if (error) return null;
      return data as Company;
    },
    enabled: !!assignment?.company_id,
  });

  const { data: jobSearch } = useQuery({
    queryKey: ["search", assignment?.search_id],
    queryFn: async (): Promise<JobSearch | null> => {
      if (!assignment?.search_id) return null;
      const { data, error } = await supabase
        .from("searches")
        .select("id, puesto, experience")
        .eq("id", assignment.search_id)
        .single();
      if (error) return null;
      return data as JobSearch;
    },
    enabled: !!assignment?.search_id,
  });

  const { data: submissions = [], isLoading: loadingSubs } = useQuery({
    queryKey: ["submissions", assignmentId],
    queryFn: async (): Promise<SubmissionRow[]> => {
      const { data, error } = await supabase
        .from("submissions")
        .select("*")
        .eq("assignment_id", assignmentId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SubmissionRow[];
    },
  });

  const resendMutation = useMutation({
    mutationFn: () => resendInvitation(assignmentId),
    onSuccess: () => {
      setFeedback({ kind: "success", title: "Email reenviado" });
      qc.invalidateQueries({ queryKey: ["assignment", assignmentId] });
    },
    onError: (e: Error) =>
      setFeedback({ kind: "error", title: "No se pudo reenviar", detail: e.message }),
  });

  if (loadingAssignment) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !assignment) {
    return (
      <div className="px-6 py-5">
        <StatusBanner variant="error" title="No se pudo cargar la invitación">
          {error?.message ?? "Assignment no encontrado"}
        </StatusBanner>
        <div className="mt-4">
          <Button variant="default" asChild>
            <Link to="/invitations">
              <ArrowLeft className="h-3.5 w-3.5" />
              Volver al listado
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const status = deriveStatus(assignment);
  const canResend = !assignment.completed_at && !assignment.invalidated_at;
  const testUrl = `${window.location.origin}/?a=${assignment.id}`;
  const finalSubmissions = submissions.filter((s) => s.is_final);

  return (
    <div className="px-6 py-5">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/invitations"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-3 w-3" />
          Invitaciones
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-semibold tracking-tight">
                {assignment.candidate_name}
              </h1>
              <Badge variant={statusVariant(status)}>
                {statusLabel(status)}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {assignment.candidate_email}
            </p>
            {(company || jobSearch) && (
              <div className="flex flex-wrap items-center gap-1.5 mt-2 text-xs">
                {company && (
                  <Link
                    to="/clients/$clientId"
                    params={{ clientId: company.id }}
                    className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Building2 className="h-3 w-3" />
                    {company.name}
                  </Link>
                )}
                {company && jobSearch && (
                  <span className="text-muted-foreground/50">·</span>
                )}
                {jobSearch && (
                  <Link
                    to="/searches/$searchId"
                    params={{ searchId: jobSearch.id }}
                    className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Briefcase className="h-3 w-3" />
                    {jobSearch.puesto}
                  </Link>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <CopyLinkButton url={testUrl} />
            {canResend && (
              <Button
                variant="default"
                onClick={() => resendMutation.mutate()}
                loading={resendMutation.isPending}
              >
                <RefreshCw className="h-4 w-4" />
                {assignment.sent_email_at ? "Reenviar email" : "Enviar email"}
              </Button>
            )}
            <Button variant="ghost" asChild>
              <a href={testUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4" />
                Abrir test
              </a>
            </Button>
          </div>
        </div>
      </div>

      {feedback && (
        <div className="mb-6">
          <StatusBanner
            variant={feedback.kind}
            title={feedback.title}
            onDismiss={() => setFeedback(null)}
          >
            {feedback.detail}
          </StatusBanner>
        </div>
      )}

      {/* Invalidated banner */}
      {assignment.invalidated_at && (
        <div className="mb-6">
          <StatusBanner variant="error" title="Test invalidado">
            <p>
              Motivo:{" "}
              <strong>
                {assignment.invalidation_reason ?? "Sin motivo registrado"}
              </strong>
            </p>
            <p className="mt-1">
              Violaciones registradas: {assignment.violation_count}
            </p>
          </StatusBanner>
        </div>
      )}

      {/* === Stat cards (resumen ejecutivo) === */}
      <StatCards assignment={assignment} submissions={submissions} />

      {/* === Violations history (si hay) === */}
      {assignment.violations && assignment.violations.length > 0 && (
        <ViolationsCard violations={assignment.violations} />
      )}

      {/* Timeline + Ejercicios */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 rounded-[var(--radius)] border border-border bg-card p-5">
          <h2 className="text-sm font-semibold tracking-tight mb-3">Timeline</h2>
          <TimelineList assignment={assignment} />
        </div>
        <div className="rounded-[var(--radius)] border border-border bg-card p-5">
          <h2 className="text-sm font-semibold tracking-tight mb-3">
            Ejercicios ({exercises.length})
          </h2>
          <ul className="space-y-2">
            {exercises.map((e) => {
              const finalSub = finalSubmissions.find((s) => s.exercise_id === e.id);
              return (
                <li
                  key={e.id}
                  className="border border-border rounded-md p-2.5 text-xs"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium truncate">{e.title}</span>
                    {finalSub ? (
                      <ScoreBadge score={finalSub.score} />
                    ) : (
                      <span className="text-[10px] text-muted-foreground">
                        Sin entregar
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate">
                    {e.id} · {e.language} · {e.duration_min}min
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* Submissions por ejercicio */}
      <div className="space-y-6">
        <h2 className="text-sm font-semibold tracking-tight">
          Submissions ({submissions.length})
        </h2>
        {loadingSubs ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : submissions.length === 0 ? (
          <div className="rounded-[var(--radius)] border border-dashed border-border bg-card/50 py-10 text-center text-sm text-muted-foreground">
            Todavía no hay submissions de este candidato.
          </div>
        ) : (
          exercises.map((ex) => {
            const exSubs = submissions.filter((s) => s.exercise_id === ex.id);
            if (exSubs.length === 0) return null;
            return (
              <ExerciseSubmissionsCard key={ex.id} exercise={ex} submissions={exSubs} />
            );
          })
        )}
      </div>
    </div>
  );
}

// =================== TIMELINE ===================
function TimelineList({ assignment }: { assignment: Assignment }) {
  const events: { icon: React.ReactNode; label: string; ts: string | null }[] = [
    {
      icon: <Clock className="h-3 w-3 text-muted-foreground" />,
      label: "Invitación creada",
      ts: assignment.created_at,
    },
    {
      icon: <Clock className="h-3 w-3 text-info" />,
      label: "Email enviado",
      ts: assignment.sent_email_at,
    },
    {
      icon: <Clock className="h-3 w-3 text-warning" />,
      label: "Test iniciado",
      ts: assignment.started_at,
    },
    {
      icon: <CheckCircle2 className="h-3 w-3 text-success" />,
      label: "Test completado",
      ts: assignment.completed_at,
    },
    {
      icon: <ShieldAlert className="h-3 w-3 text-destructive" />,
      label: "Test invalidado",
      ts: assignment.invalidated_at,
    },
    {
      icon: <XCircle className="h-3 w-3 text-destructive" />,
      label: "Tiempo agotado",
      ts: assignment.timed_out_at,
    },
  ];
  const hasExpiry = !!assignment.expires_at;

  return (
    <ul className="space-y-2.5 text-xs">
      {events.map((e) =>
        e.ts ? (
          <li key={e.label} className="flex items-center gap-2.5">
            {e.icon}
            <span className="text-muted-foreground">{e.label}</span>
            <span className="ml-auto text-foreground/80 font-mono">
              {formatDateTime(e.ts)}
            </span>
          </li>
        ) : null
      )}
      {hasExpiry && (
        <li className="flex items-center gap-2.5 pt-2 border-t border-border mt-2">
          <Clock className="h-3 w-3 text-muted-foreground/60" />
          <span className="text-muted-foreground">Vencimiento</span>
          <span className="ml-auto text-foreground/80 font-mono">
            {formatDateTime(assignment.expires_at!)}
          </span>
        </li>
      )}
    </ul>
  );
}

// =================== SUBMISSIONS POR EJERCICIO ===================
function ExerciseSubmissionsCard({
  exercise,
  submissions,
}: {
  exercise: Exercise;
  submissions: SubmissionRow[];
}) {
  const finalSub = submissions.find((s) => s.is_final);
  const [expandedId, setExpandedId] = useState<string | null>(
    finalSub?.id ?? submissions[0]?.id ?? null
  );

  return (
    <div className="rounded-[var(--radius)] border border-border bg-card overflow-hidden">
      <div className="px-5 py-3 border-b border-border flex items-center justify-between">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold truncate">{exercise.title}</h3>
          <div className="text-[11px] text-muted-foreground font-mono mt-0.5 truncate">
            {exercise.id} · {exercise.language}
          </div>
        </div>
        {finalSub && <ScoreBadge score={finalSub.score} />}
      </div>

      <ul className="divide-y divide-border">
        {submissions.map((s) => {
          const expanded = expandedId === s.id;
          return (
            <li key={s.id}>
              <button
                type="button"
                onClick={() => setExpandedId(expanded ? null : s.id ?? null)}
                className="w-full px-5 py-3 flex items-center gap-3 hover:bg-accent/40 transition-colors text-left"
              >
                <span className="font-mono text-[11px] text-muted-foreground shrink-0 w-12">
                  #{s.attempt_n}
                </span>
                <ScoreBadge score={s.score} compact />
                <span className="text-xs text-muted-foreground shrink-0">
                  {s.passed_count}/{s.total_count} tests
                </span>
                {s.is_final && (
                  <Badge variant="success" className="text-[9px]">
                    final
                  </Badge>
                )}
                <span className="ml-auto text-[11px] text-muted-foreground shrink-0">
                  {s.created_at ? formatDateTime(s.created_at) : ""}
                </span>
              </button>
              {expanded && <SubmissionDetails submission={s} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SubmissionDetails({ submission }: { submission: SubmissionRow }) {
  const [copied, setCopied] = useState(false);
  function copyCode() {
    navigator.clipboard.writeText(submission.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <div className="bg-secondary/20 border-t border-border">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Tests */}
        <div className="p-4 border-r border-border">
          <h4 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            Tests
          </h4>
          <ul className="space-y-1.5 text-xs">
            {submission.test_results.map((t, i) => (
              <li key={i} className="flex items-start gap-2">
                {t.passed ? (
                  <Check className="h-3 w-3 text-success mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-3 w-3 text-destructive mt-0.5 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div
                    className={cn(
                      t.passed ? "text-foreground/80" : "text-destructive"
                    )}
                  >
                    {t.name}
                  </div>
                  {t.error && (
                    <div className="text-[10px] text-muted-foreground font-mono mt-0.5 break-all">
                      └─ {t.error}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Code */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
              Código entregado
            </h4>
            <Button size="iconSm" variant="ghost" onClick={copyCode}>
              {copied ? (
                <Check className="h-3.5 w-3.5 text-success" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>
          <pre className="bg-background border border-border rounded-md p-3 text-[11px] font-mono overflow-auto max-h-[400px] text-foreground/90">
            <code>{submission.code}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}

// =================== SCORE BADGE ===================
function ScoreBadge({
  score,
  compact = false,
}: {
  score: number;
  compact?: boolean;
}) {
  const variant = score >= 80 ? "success" : score >= 50 ? "warning" : "danger";
  return (
    <Badge variant={variant} className={cn("shrink-0", compact && "min-w-[44px] justify-center")}>
      {score}%
    </Badge>
  );
}

// =================== COPY LINK ===================
function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }
  return (
    <Button variant="default" onClick={copy}>
      {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copiado" : "Copiar link"}
    </Button>
  );
}

// =================== STAT CARDS ===================
function StatCards({
  assignment,
  submissions,
}: {
  assignment: Assignment;
  submissions: SubmissionRow[];
}) {
  const finalSubs = submissions.filter((s) => s.is_final);
  const bestScore =
    finalSubs.length > 0 ? Math.max(...finalSubs.map((s) => s.score)) : null;
  const attempts = submissions.length;
  const violations = assignment.violation_count ?? 0;

  // Duración: started_at → completed_at (o invalidated_at o timed_out_at)
  const startTs = assignment.started_at
    ? new Date(assignment.started_at).getTime()
    : null;
  const endTs =
    assignment.completed_at || assignment.invalidated_at || assignment.timed_out_at;
  const durationMs =
    startTs && endTs ? new Date(endTs).getTime() - startTs : null;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
      <StatCard
        label="Score final"
        value={bestScore != null ? `${bestScore}%` : "—"}
        accent={
          bestScore == null
            ? "neutral"
            : bestScore >= 80
              ? "success"
              : bestScore >= 50
                ? "warning"
                : "danger"
        }
      />
      <StatCard
        label="Intentos"
        value={String(attempts)}
        accent="neutral"
      />
      <StatCard
        label="Violaciones"
        value={String(violations)}
        accent={violations === 0 ? "neutral" : violations === 1 ? "warning" : "danger"}
      />
      <StatCard
        label="Duración"
        value={durationMs != null ? formatDuration(durationMs) : "—"}
        accent="neutral"
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "neutral" | "success" | "warning" | "danger";
}) {
  const accentClasses = {
    neutral: "text-foreground",
    success: "text-success",
    warning: "text-warning",
    danger: "text-destructive",
  }[accent];
  return (
    <div className="rounded-[var(--radius)] border border-border bg-card p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      <div className={cn("text-2xl font-bold tracking-tight mt-1", accentClasses)}>
        {value}
      </div>
    </div>
  );
}

// =================== VIOLATIONS HISTORY ===================
function ViolationsCard({
  violations,
}: {
  violations: NonNullable<Assignment["violations"]>;
}) {
  const labels: Record<string, string> = {
    tab_switch: "Cambio de pestaña / app",
    window_blur: "Foco fuera de la ventana",
  };
  return (
    <div className="rounded-[var(--radius)] border border-destructive/30 bg-destructive/5 p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <ShieldAlert className="h-4 w-4 text-destructive" />
        <h2 className="text-sm font-semibold tracking-tight text-destructive">
          Historial anti-cheat ({violations.length})
        </h2>
      </div>
      <ul className="space-y-2">
        {violations.map((v, i) => (
          <li
            key={i}
            className="flex items-center justify-between gap-3 text-sm rounded-md border border-destructive/20 bg-card px-3 py-2"
          >
            <span className="flex items-center gap-2">
              <span className="font-mono text-[10px] text-muted-foreground bg-secondary px-1.5 py-0.5 rounded">
                #{i + 1}
              </span>
              <span>{labels[v.kind] ?? v.kind}</span>
            </span>
            <span className="text-[11px] text-muted-foreground font-mono">
              {formatDateTime(v.at)}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-muted-foreground mt-3">
        La primera violación dispara un aviso al candidato. La segunda invalida
        el test automáticamente.
      </p>
    </div>
  );
}

// =================== HELPERS ===================
function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "short",
    year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDuration(ms: number): string {
  if (ms < 0) return "—";
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 1) return `${sec}s`;
  return `${min}m ${sec}s`;
}

